import { Money } from "@redotech/money";
import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { GetCheckoutTreeAnalyticsOutput } from "@redotech/redo-merchant-conversion-rpc/schema/get-checkout-tree-analytics";
import { createContext, ReactNode, useMemo } from "react";

interface AnalyticsForCheckoutNodeResult {
  total: { orders: { count: number }; revenue: Money; shippingRevenue: Money };
  byRateTableId: Record<
    string,
    {
      orders: { count: number; relativePercentage: number };
      revenue: Money;
      shippingRevenue: Money;
    }
  >;
}

interface CheckoutTreeAnalytics {
  analyticsForCheckoutNodeByNodeId: Record<
    string,
    AnalyticsForCheckoutNodeResult
  >;
  checkoutTreeAnalyticsLoad: LoadState<
    GetCheckoutTreeAnalyticsOutput | undefined
  >;
}

export const CheckoutTreeAnalyticsContext =
  createContext<CheckoutTreeAnalytics>({
    analyticsForCheckoutNodeByNodeId: {},
    checkoutTreeAnalyticsLoad: { pending: false, value: undefined },
  });

export function CheckoutTreeAnalyticsProvider({
  children,
  checkoutTreeId,
}: {
  children?: ReactNode | ReactNode[];
  checkoutTreeId: { kind: "split"; splitId: string } | { kind: "current" };
}) {
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );

  const checkoutTreeAnalyticsLoad = useLoad(async () => {
    const checkoutTreeAnalytics =
      await conversionRpcClient.getCheckoutTreeAnalytics({ checkoutTreeId });
    return checkoutTreeAnalytics ?? undefined;
  }, [checkoutTreeId]);

  const analyticsForCheckoutNodeByNodeId: Record<
    string,
    AnalyticsForCheckoutNodeResult
  > = useMemo(() => {
    return (
      checkoutTreeAnalyticsLoad.value?.analyticsForCheckoutNodeByNodeId ?? {}
    );
  }, [checkoutTreeAnalyticsLoad.value]);

  const value = useMemo(
    () => ({ analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad }),
    [analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad],
  );

  return (
    <CheckoutTreeAnalyticsContext.Provider value={value}>
      {children}
    </CheckoutTreeAnalyticsContext.Provider>
  );
}
